import axios, { AxiosResponse, AxiosError } from 'axios';
import { head } from 'lodash';
import { api } from '../../config';
import { getLocalToken } from '../../utils/helpers';
import { url } from '../router-service';
import services from 'services';

export interface ApiError {
    code: number; 
    message: string;    
}

export class ApiResponse<T = any> {
    public status: number;
    public data: T;
    public headers?: any;
    public message?: string;
    public isSuccess: boolean;
    public isError: boolean;
    public error?: ApiError;

    constructor(status: number, responseBody: any, headers?: any) {
        this.status = status;
        this.headers = headers;

        const isStandardResponse = responseBody && typeof responseBody.isSuccess === 'boolean';

        if (isStandardResponse) {
            this.isSuccess = responseBody.isSuccess;
            
            if (responseBody.data !== undefined && responseBody.data !== null) {
                this.data = responseBody.data; 
            } else {
                this.data = responseBody; 
            }
            
            this.message = responseBody.message;
            this.error = responseBody.error;
        } else {                
            this.isSuccess = status >= 200 && status <= 299;
            this.data = responseBody;
            this.message = responseBody?.message || (typeof responseBody === 'string' ? responseBody : undefined);
        }

        this.isError = !this.isSuccess;
    }

    public get(key: string) {
        return this.data && (this.data as any)[key];
    }

    public getErrorList(): string[] {
        if (this.error) {
            return [this.error.message];
        }
        if (this.message && !this.isSuccess) {
            return [this.message];
        }        
        if ((this.data as any)?.errors && Array.isArray((this.data as any).errors)) {
            return (this.data as any).errors.map((e: any) => e.msg || e.message);
        }
        return [];
    }

    public getError(): string {
        return this.getErrorList().join(' ') || this.message || 'Unknown Error';
    }   
}

export const request = <T = any>(options: any): Promise<ApiResponse<T>> => {
    const headers: any = {
        Accept: 'application/json',
    };
    if (options.method && options.method.toUpperCase() === 'PATCH') {
        headers['Content-Type'] = 'application/json-patch+json';
    }

    const accessToken = getLocalToken();     
    if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`; 
    }
    if (options.headers && options.headers.Authorization) {
        headers.Authorization = options.headers.Authorization;
    }

    return axios
        .request({
            baseURL: api.endpoint,
            responseType: 'json',
            ...options,
            headers: {
                ...headers,
                ...options.headers,
            },
        })
        .then((response: AxiosResponse) => {
            const { status, data, headers } = response;
            return new ApiResponse<T>(status, data, headers);
        })
        .catch((error: AxiosError) => {
            if (error.response) {
                const { status, data, headers } = error.response;

                if (status === 401 || status === 403) {
                    const currentPath = window.location.pathname;
                    
                    // ONLY clear data and redirect if we aren't already on the login page
                    // This allows the probe call in handleLogin to fail gracefully
                    if (currentPath !== url.login && currentPath !== '/login_callback') {
                        console.warn("Session invalid or inactive. Clearing local data.");
                        localStorage.removeItem('token');
                        localStorage.removeItem('userInfo');
                        services.router.goto(url.login);
                    }
                }
                return new ApiResponse<T>(status, data, headers);
            }            
            return new ApiResponse<T>(400, { message: "Network Error: Please check your connection." });
        });
};
/*
axios.interceptors.response.use(request => {
    console.log('Starting Request', JSON.stringify(request, null, 2)); 
    return request; 
}); 
*/